// Board tile reading: the cards a board square represents, for the large card slot.
// Order: the square's main card (tile, or the House Cube card for a building), then its
// top token (Green Roof), its Green Street, then the Sea Walls built on its edges (TERRAIN 2 of 3).
import { nameOf, pieceLabel } from "@/components/game/SeaSquare";
import { WALL_KEYS } from "@/components/game/CellTokens";
import { wallText } from "@/components/game/EdgeWalls";
import { squareShort } from "@/lib/squares";
import { heightLine } from "@/lib/terrain";

const titleCase = (key) => (key || "card").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function cardFor(defs, cellKey, key, name, n) {
  const def = (defs || []).find((d) => d.key === key);
  return { ...(def || { key, name: name || titleCase(key), kind: "build" }), id: `board-${cellKey}-${n}-${key}` };
}

const floorsText = (n) => `${n} floor${n === 1 ? "" : "s"}`;

// -> null (nothing to read) | { cellKey, caption, entries: [{card, label, broken}], note }
// gridRows: the board's row count (square label). height: the square's terrain height -> a
// "Height ▲2" line on every tab. walls: game.walls (the walls built on this square get a tab).
export function tileCardsFor(cellKey, cell, players = [], defs = [], gridRows = null, height = null, walls = null) {
  if (!cell?.kind) return null;
  if (cell.kind === "sea_marker") {
    const d = cell.drowned;
    if (!d) return null;
    return {
      cellKey,
      label: gridRows ? squareShort(cellKey, gridRows) : cellKey,
      caption: null,
      entries: [],
      note: { title: `Taken by the sea in round ${d.round}`, sub: `Was ${nameOf(players, d.owner)}'s ${pieceLabel(d)}` },
    };
  }
  if (cell.kind !== "tile" && cell.kind !== "cube") return null;
  const owner = nameOf(players, cell.owner);
  const entries = [];
  let caption = owner;
  if (cell.kind === "cube") {
    const n = (cell.cube_stack || []).length;
    entries.push({ card: cardFor(defs, cellKey, "house_cube", "House Cube", 0), label: "House Cube" });
    caption = `${owner} · ${floorsText(n)}`;
  } else {
    const card = cardFor(defs, cellKey, cell.card_key, cell.card_name, 0);
    entries.push({ card, label: card.name });
    if (cell.submerged) caption = `${owner} · submerged`;
  }
  if (cell.token?.card_key) {
    const card = cardFor(defs, cellKey, cell.token.card_key, cell.token.card_name, 1);
    entries.push({ card, label: card.name });
  }
  (cell.edge_tokens || []).forEach((e, i) => {
    if (!e?.card_key) return;
    if (WALL_KEYS.has(e.card_key)) return;
    const card = cardFor(defs, cellKey, e.card_key, e.card_name, 2 + i);
    entries.push({ card, label: card.name, broken: !!e.sabotage_marker });
  });
  const order = { top: 0, right: 1, bottom: 2, left: 3 };
  Object.values(walls || {})
    .filter((w) => w?.square === cellKey)
    .sort((a, b) => order[a.side] - order[b.side])
    .forEach((w, i) => {
      const card = cardFor(defs, cellKey, "low_sea_wall", "Sea Wall", 10 + i);
      entries.push({ card, label: `Sea Wall · ${w.side} edge`, broken: !!w.sabotaged, detail: [wallText(w, players)] });
    });
  if (height != null) entries.forEach((en) => { en.detail = [...(en.detail || []), heightLine(height)]; });
  return { cellKey, label: gridRows ? squareShort(cellKey, gridRows) : cellKey, caption, color: cell.owner, entries, note: null };
}
